'use client'

import { useAuthorization } from '@entities/authentication'
import type { KrPaymentOrder } from '@entities/kr-payment/model'
import { PaymentMethodType } from '@entities/payment-methods'
import { userEntityQueries } from '@entities/users'
import {
  useCheckPaymentStatusPolling,
  usePaymentByCreditCard,
  usePaymentByKakaoPay,
  usePaymentQueue,
  usePaymentValidation,
} from '@features/payment'
import { useRegisterCardFormState } from '@features/payment-methods'
import { PaymentType, SubscribeTicket } from '@server/schemas'
import { env } from '@shared/config/env'
import { success, withAsyncPipeline, withSuccessCallback } from '@shared/libs'
import { sendGA4PurchaseEvent } from '@shared/libs/gtm'
import { useNativeBridgeStore } from '@shared/models'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { useMemo, useRef } from 'react'

import { useKrPreparedPayment } from './use-kr-prepared-payment'

type PaymentInfo = {
  isTermsAgreed: boolean
  paymentType: PaymentType
  paymentMethodType: Nullable<PaymentMethodType>
  customerUid: Nullable<string>
  amount: number
  subscribeTicket: SubscribeTicket
  merchantName: string
  productId?: string
  krPreparedCheckout?: boolean
  preparedOrderStatus?: KrPaymentOrder['status']
}

export const useSmartTalkTrialPayment = ({
  isTermsAgreed,
  paymentType,
  paymentMethodType,
  customerUid,
  subscribeTicket,
  merchantName,
  productId,
  krPreparedCheckout = false,
  preparedOrderStatus,
}: PaymentInfo) => {
  const authorization = useAuthorization()
  const router = useRouter()
  const nativeStore = useNativeBridgeStore()

  const { data: userInfo } = useSuspenseQuery({
    ...userEntityQueries.getCurrentUser({ bearerToken: authorization.bearerToken }),
  })

  const sendPurchaseEvent = (): 'success' | 'error' => {
    // GTM 커스텀 purchase 이벤트 전송
    sendGA4PurchaseEvent({
      transactionId: `T_${Date.now()}${Math.random()}`,
      value: subscribeTicket?.subPrice ?? 0,
      currency: 'KRW',
      items: [
        {
          itemId: `podo_trial_smarttalk_${subscribeTicket?.langType}`,
          itemName: `podo_trial_smarttalk_${subscribeTicket?.langType}`,
          itemCategory: 'Trial',
          price: subscribeTicket?.subPrice ?? 0,
          quantity: 1,
        },
      ],
      userId: userInfo?.id,
      userName: userInfo?.name,
      userPhone: userInfo?.phone,
      userEmail: userInfo?.email,
    })

    return success()
  }

  const hasSentPurchaseEventRef = useRef(false)
  const sendPurchaseEventOnce = (): 'success' | 'error' => {
    if (hasSentPurchaseEventRef.current) {
      return success()
    }
    hasSentPurchaseEventRef.current = true
    return sendPurchaseEvent()
  }

  const { pollingState, setPollingState } = useCheckPaymentStatusPolling({
    onSuccess: async () => {
      if (krPreparedCheckout) {
        sessionStorage.removeItem(`podo:kr-purchase:${authorization.uid}:${subscribeTicket.id}`)
      }
      sendPurchaseEventOnce()
      await new Promise((resolve) => setTimeout(resolve, 300))
      router.replace('/home/ai')
    },
  })

  const createNotificationWebhookUrl = (): URL => {
    const webhookUrl = new URL(`${env.NEXT_PUBLIC_PODO_API_URL}/api/v1/payment/podo/webhook`)

    webhookUrl.searchParams.set('user_id', authorization.uid.toString())
    webhookUrl.searchParams.set('subscribe_id', subscribeTicket.id)
    webhookUrl.searchParams.set('trial_level', 'SMART_TALK')
    if (productId) {
      webhookUrl.searchParams.set('product_id', productId)
    }
    webhookUrl.searchParams.set('payment_type', 'TRIAL')

    return webhookUrl
  }

  const billingFailSlackMessage = (errorCode?: string, errorMessage?: string) => {
    return ` \`[Smart Talk trial lesson ticket payment API failed]\` User: ${userInfo.name}(${userInfo.id}) / Amount: ₩${subscribeTicket.subPrice.toLocaleString()}\n
    [${errorCode}] ${errorMessage}\n\n
    \`System info\`\n
      - \`OS\` : ${nativeStore.os}\n
      - \`UserAgent\` : ${nativeStore.userAgent}\n
      - \`RuntimeVersion\` : ${nativeStore.runtimeVersion}\n
      - \`from\` : ${nativeStore.from}\n`
  }

  const handlePaymentQueue = usePaymentQueue({
    handleEnabledPolling: (merchantUid) =>
      setPollingState({
        status: 'in-progress',
        data: { merchantUid, ...(krPreparedCheckout ? { purchaseOrderId: merchantUid } : {}) },
      }),
  })

  const { formState: registerCardFormState } = useRegisterCardFormState()

  const { validateClientSideRegisterCard, validateServerSideRegisterCard, validatePayment } = usePaymentValidation({
    isTermsAgreed,
    paymentMethodType,
    registerCardFormState,
    paymentType,
  })

  const preparePayment = useKrPreparedPayment({
    enabled: krPreparedCheckout,
    subscribeId: subscribeTicket.id,
    productId,
    amount: subscribeTicket.subPrice,
    couponId: null,
    paymentMethod: paymentMethodType,
    isLumpSum: false,
    trialLevel: 'SMART_TALK',
    onPending: (merchantUid) =>
      setPollingState({ status: 'in-progress', data: { merchantUid, purchaseOrderId: merchantUid } }),
  })

  const customData = useMemo(
    () => ({
      user_id: authorization.uid.toString(),
      subscribe_id: subscribeTicket.id,
      product_id: productId,
      coupon_id: null,
      is_v2_validated: false,
    }),
    [authorization.uid, subscribeTicket.id, productId],
  )

  const { handlePaymentForNewCreditCard, handlePaymentForRegisteredCreditCard } = usePaymentByCreditCard({
    preparePayment,
    handlePaymentQueue,
    validatePayment,
    validateClientSideRegisterCard,
    validateServerSideRegisterCard,
    customerUid,
    merchantName: merchantName,
    amount: subscribeTicket.subPrice ?? 0,
    customData,
    paymentType: 'TRIAL',
    billingFailSlackMessage,
    paymentWebhookUrl: createNotificationWebhookUrl(),
  })

  const { handlePaymentForNewKakaoPay, handlePaymentForRegisteredKakaoPay } = usePaymentByKakaoPay({
    preparePayment,
    validatePayment,
    handlePaymentQueue,
    customerUid,
    merchantName: merchantName,
    amount: subscribeTicket.subPrice,
    customData,
    paymentType: 'TRIAL',
    billingFailSlackMessage,
    couponId: null,
    paymentWebhookUrl: createNotificationWebhookUrl(),
    mobileRedirectSearchParams: {
      __brand: 'TRIAL',
      subscribe_id: subscribeTicket.id,
      trial_level: 'B',
    },
  })

  return {
    resumePreparedPayment:
      krPreparedCheckout &&
      preparedOrderStatus &&
      ['PROCESSING', 'FULFILLMENT_PENDING', 'PAID'].includes(preparedOrderStatus)
        ? async () => {
            await preparePayment?.()
          }
        : undefined,
    handlePaymentForNewCreditCard: withAsyncPipeline(
      handlePaymentForNewCreditCard,
      withSuccessCallback(() => (krPreparedCheckout ? success() : sendPurchaseEventOnce())),
    ),
    handlePaymentForRegisteredCreditCard: withAsyncPipeline(
      handlePaymentForRegisteredCreditCard,
      withSuccessCallback(() => (krPreparedCheckout ? success() : sendPurchaseEventOnce())),
    ),
    handlePaymentForNewKakaoPay: withAsyncPipeline(
      handlePaymentForNewKakaoPay,
      withSuccessCallback(() => (krPreparedCheckout ? success() : sendPurchaseEventOnce())),
    ),
    handlePaymentForRegisteredKakaoPay: withAsyncPipeline(
      handlePaymentForRegisteredKakaoPay,
      withSuccessCallback(() => (krPreparedCheckout ? success() : sendPurchaseEventOnce())),
    ),
    validatePayment,
    pollingState,
  }
}
